import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  MeetingSubjectsResponse,
  deleteMeetingSubject,
  getMeetingSubjectsByProjectMeetingId,
} from '@/services/MeetingSubjectsService';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { SubjectStatus } from '@/enums/SubjectStatus';

const userTimeZone = moment.tz.guess();

function MeetingSubjects() {
  const { meetingId } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('MeetingSubjects.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('MeetingSubjects.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteMeetingSubject.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('MeetingSubjects.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteMeetingSubject}
          />
        ),
      },
      {
        Header: t('MeetingSubjects.descriptionColumn'),
        accessor: 'description',
      },
      {
        Header: t('MeetingSubjects.responsibleNameColumn'),
        accessor: 'responsibleName',
      },
      {
        Header: t('MeetingSubjects.deadlineColumn'),
        accessor: 'deadline',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = moment(value).format('YYYY-MM-DD'); // Customize the format as needed
          return (
            <input
              type="text"
              className="min-w-40 dark:bg-boxdark-2 dark:text-bodydark"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('MeetingSubjects.progressColumn'),
        accessor: 'progressPercentage',
      },
      {
        Header: t('MeetingSubjects.responsibleCommentsColumn'),
        accessor: 'responsibleComments',
      },
      {
        Header: t('MeetingSubjects.responsibleSubjectProgressColumn'),
        accessor: 'responsibleSubjectProgress',
      },
      {
        Header: t('MeetingSubjects.statusColumn'),
        accessor: 'status',
        Cell: ({ cell: { value } }) =>
          t(`SubjectStatus.${SubjectStatus[value]}`),
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: meetingSubjects,
  } = useQuery<MeetingSubjectsResponse | undefined>(
    ['meetingSubjects', pageIndex, pageSize, search, meetingId],
    () =>
      getMeetingSubjectsByProjectMeetingId({
        meetingId: meetingId!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
    }
  );

  const handleDeleteMeetingSubject = useMutation(
    async (id: string) => {
      await deleteMeetingSubject(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'meetingSubjects',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil((meetingSubjects?.meetingSubjects.length ?? 0) / pageSize);
  }, [pageSize, meetingSubjects?.meetingSubjects]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('MeetingSubjects.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={meetingSubjects?.meetingSubjects.length}
            data={meetingSubjects?.meetingSubjects ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default MeetingSubjects;
